
/**
 * Solr.js
 *
 * Responsible for constructing SOLR queries based on Widget options / configuration,
 * fetch queries from SOLR, and translate responses back to the WidgetModel's fields
 *
 */

import axios from 'axios';
import utils from '../../common/utils.js';
import cache from './cache.js';
import config from '../../common/config.js';
import md5 from 'md5';
import jsonpAdapter from 'axios-jsonp';
import SolrContentConfig from './SolrContentConfig.js';

cache.queries = {};

// limit how many queries we cache, the results can add up in memory
const CACHE_LIMIT = 10;
const POST_LIMIT = 1000;
const FACET_LIMIT = 2000;
const AND = 'AND';
const OR = 'OR';
const $ = window && window.jQuery;

const CONTENT_TYPE_FILE = 'File';
const CONTENT_TYPE_DFE = 'DFE Page';

class Solr {
	widgetId;
	widgetOptions = {};
	widgetModel;
	taxOptions;
	solrContentConfig = {};
	inEditor = false;
	queryIndex = 0;
	solrURL = '';
	solrInternal = false;
	validQuery = true;
	queryCount = 0;
	postId = 0;
	siteId = 0;

	// properties from widgetOptions
	pagination = false;
	pageSize = 10;
	limit = 0;

	queryBase = {
		q: '*:*',
		fq: [],
		wt: 'json',
		start: 0,
		rows: 10, // @TODO: use model widgetOptions
		fl: ['id', 'type', 'type_txt'],
	};

	/**
	 * SOLR service constructor
	 * @param {WidgetModel} widgetModel
	 */
	constructor(widgetModel) {
		this.widgetModel = widgetModel;
		this.widgetId = widgetModel.id;
		this.inEditor = widgetModel.inEditor;

		config.debug && console.info('cc config: ', config, this.widgetId, this.inEditor);

		this.solrURL = utils.cleanString(config.solrURL).replace(/\s+/, '');

		this.widgetOptions = Object.assign({}, widgetModel.options);

		// copy some properties from WidgetModel
		this.taxOptions = widgetModel.taxOptions;
		this.pageSize = widgetModel.pageSize;
		this.pagination = widgetModel.pagination;
		this.limit = widgetModel.limit;

		// get content configuration from given widget options
		this.solrContentConfig = new SolrContentConfig(this.widgetOptions);

		// we have to send the solrContentConfig object / data to taxOption
		// so it can map some special filters to solr fields
		this.taxOptions.setSolrContentConfig(this.solrContentConfig);

		// start compiling the base SOLR query
		let types = utils.toArray(this.solrContentConfig.types).slice();
		let fields = this.solrContentConfig.fields;

		// No content types selected means bad query
		if (!types?.length) {
			this.validQuery = false;
		}
		// use 'type_txt' because "Image" is actually 'type: attachment'
		// hack until figure out how to use nested query for File Attachments
		// attachment type filter -- only gets added if File content type is selected
		const typesQuery = [];
		if ( types.includes(CONTENT_TYPE_FILE) ) {
			// Get Valid MIME Types
			let mimeTypes = [];
			this.widgetOptions.status.fileTypes.forEach( ( fileType ) => {
				if (fileType.ext.length) {
					fileType.ext.forEach((ext) => mimeTypes.push( `"${utils.getMIME( ext )}"` ));
				}
			} );
			if( mimeTypes.length) {
				types.splice(types.indexOf(CONTENT_TYPE_FILE), 1);
				// Add the MIME Types as an additional filter query, leave File in original query
				typesQuery.push(`(type_txt:"${CONTENT_TYPE_FILE}" AND mime_type:(${[...mimeTypes]}))`);
			}
		}
		// if DFE Templates are specified
		if ( types.includes(CONTENT_TYPE_DFE)) {
			let dfeTemplates = utils.toArray(this.widgetOptions.status.dfeTemplates);
			if (dfeTemplates.length) {
				types.splice(types.indexOf(CONTENT_TYPE_DFE), 1);
				let templates = dfeTemplates.map(template => `"${template}"`).join(',');
				typesQuery.push(`(type_txt:"${CONTENT_TYPE_DFE}" AND cdc_dfe_template_str:(${templates}))`);
			}
		}
		if (types.length) {
			// use 'type_txt' because "Image" is actually 'type: attachment'
			typesQuery.push(this._objToQuery({type_txt: types}));
		}
		if (typesQuery.length) {
			if (1 < typesQuery.length) {
				this.queryBase.fq.push(this._arrayToQuery(typesQuery, this.OR));
			} else {
				this.queryBase.fq.push(typesQuery[0]);
			}
		}

		if (fields) {
			this.queryBase.fl = this.queryBase.fl.concat(fields);
		} else {
			console.error('cc: no solr fields found.', this.widgetId);
		}

		// process content sources
		const contentSources = utils.toArray(this.widgetOptions.contentSources);

		// [DEV-5508] check if we should use Beta solr core
		//            if host matches "beta" ... ".cdc.gov" (beta.cdc.gov, betaatsdr.cdc.gov, betaem.cdc.gov...) or "dfe...cdc.gov"
		if (config.solrURLBeta && (location.hostname.match(/beta.*\.cdc\.gov/) || location.hostname.match(/dfe.*\.cdc\.gov/))) {
			this.solrURL = config.solrURLBeta;
		}

		// site filter
		let siteFilterQueries = [];

		// [DEV-6436] Site Limit: Optional ability to limit displayed sites on a given environment
		//            when a site limit list exists. Bounds allowed site ids and site contexts
		let siteLimit = [];
		// 20240221: Update: only site limit on beta.cdc.gov
		if (Array.isArray(window.CDC_CONFIG?.cc_site_limit) && 'beta.cdc.gov' === String(window.location.hostname).toLowerCase().trim()) {
			siteLimit = window.CDC_CONFIG.cc_site_limit.map(item => utils.cleanString(item));
		}

		let excludedSites = [];

		// extract any "All" site queries
		let allSiteFilter = contentSources.find(contentSource => 'all' === contentSource?.site_id);
		// if one is found, it becomes the only site that matters
		if (allSiteFilter) {
			contentSources.splice(0, contentSources.length, allSiteFilter);
		}

		// Check content sources if we want dev or preview posts
		this.solrInternal = false;
		if (config.solrURLInternal) {
			contentSources.forEach((contentSource) => {
				if (contentSource.published && 'link' !== contentSource.published) {
					this.solrInternal = true;
					this.solrURL = config.solrURLInternal;
				}
			});
		}

		contentSources.forEach((contentSource) => {
			if (!contentSource) {
				return;
			}
			let siteFilterQuery = [];
			// if "all" sites, skip this part
			if (contentSource.site_id && 'all' !== contentSource.site_id) {
				let siteId = utils.getWCMSSiteId(contentSource.site_id);

				// for current site, attempt to use current found site id
				if ('current' === contentSource.site_id && config.siteId) {
					contentSource.site_id = config.siteId
				} else if (!siteId) {
					// make sure side_id is valid
					config.debug && console.error(`contentSources: invalid site ID found: ${contentSource.site_id}`);
					return;
				}

				// enforce Site Limit if its set
				if (siteLimit.length && !siteLimit.includes(String(contentSource.site_id)) && !siteLimit.includes(siteId)) {
					excludedSites.push(siteId);
					return;
				}
				if (utils.cleanString(contentSource.site_id).match(/\d+[\-\_]\d+/)) {
					siteFilterQuery.push('topical_site_context_s:' + utils.cleanString(contentSource.site_id));
				} else {
					siteFilterQuery.push('site_id:' + utils.toNumber(contentSource.site_id));
				}
			}

			// internal solr search needs qualifiers
			if (this.solrInternal) {
				if ('link' === contentSource.published) {
					siteFilterQuery.push('cdc_pub_status_link_str:("editsnotpublished","published")');
				}
				if ('dev' === contentSource.published) {
					siteFilterQuery.push('cdc_pub_status_dev_str:("editsnotpublished","published")');
				}
			}

			// folder filter
			if (contentSource.folders && contentSource.folders.length) {
				let foldersQuery = [];
				contentSource.folders.forEach((folder) => {
					if (folder && folder.name) {
						let path = utils.cleanTerm(folder.name);
						if (path) {
							foldersQuery.push('permalink:*/' + utils.trailingSlash(utils.unLeadingSlashit(path)) + '*');
						}
					}
				});
				if (foldersQuery.length) {
					siteFilterQuery.push(this._arrayToQuery(foldersQuery, 'OR'));
				}
			}
			if (siteFilterQuery.length) {
				siteFilterQueries.push(this._arrayToQuery(siteFilterQuery, 'AND'));
			}
		});
		// if sites were excluded
		if (excludedSites.length) {
			config.debug && console.info('sites excluded: ', excludedSites);
			// if all requested sites were excluded, we stop here
			if (!siteFilterQueries.length) {
				config.debug && console.error('all sites were excluded, end here');
				this.validQuery = false;
			}
		}

		// siteFilterQueries will be empty for an ALL sites query
		if (siteFilterQueries.length) {
			this.queryBase.fq.push(this._arrayToQuery(siteFilterQueries, 'OR', 'AND'));
		}

		// taxonomy filters
		const taxonomyTypes = Object.assign({}, this.widgetOptions.taxonomies);
		const taxonomies = Object.assign({}, taxonomyTypes.global, taxonomyTypes.local);

		if (taxonomies) {
			const pageTaxValues = Object.assign({}, config.postTax);

			for (let taxonomy in taxonomies) {
				let taxonomySettings = taxonomies[taxonomy];
				let matchExact = utils.toBoolean(taxonomySettings.matchExact);
				let usePageValues = utils.toBoolean(taxonomySettings.usePageValues);
				let matchAll = utils.toBoolean(taxonomySettings.matchAll);
				let excludeAll = utils.toBoolean(taxonomySettings.excludeAll);
				let matchAtLeastOne = utils.toBoolean(taxonomySettings.matchAtLeastOne);
				let subJoin = matchAll ? AND : OR;
				let join = excludeAll ? AND : OR;
				let taxFilter = {};
				let taxonomyFields = [this.solrContentConfig.getSolrField(taxonomy)];
				if (!matchExact) {
					let parentField = this.solrContentConfig.getSolrParentsField(taxonomy);
					if (parentField) {
						taxonomyFields.push(parentField);
					}
				}
				let values = [];
				let taxonomyValues = utils.toArray(taxonomySettings.terms);
				// if use page values, pull from page tax values
				if (usePageValues && pageTaxValues[taxonomy]) {
					values = utils.toArray(pageTaxValues[taxonomy]);
				} else {
					taxonomyValues.forEach((taxonomyValue) => {
						let termId = utils.cleanString(taxonomyValue.id || taxonomyValue.value || '');
						if (termId) {
							values.push(termId);
						}
					});
				}

				if (values.length) {
					taxonomyFields.forEach((taxonomyField) => {
						if(excludeAll){
							taxFilter['-' + taxonomyField] = values;
						}
						else {
							taxFilter[taxonomyField] = values;
						}
					});
					this.queryBase.fq.push(this._objToQuery(taxFilter, join, subJoin));
				}
			}
		}

		// Display options
		if (!this.widgetOptions.includePage && config.postId) {
			// don't include the current page
			this.queryBase.fq.push('-id:' + config.postId);
		}

		// @TODO: date filters
		if ( this.widgetOptions.status.dateRange.enabled ){
		 	let format = this.widgetOptions.status.dateRange.format;
			let length = this.widgetOptions.status.dateRange.length;
			// Get Today's Date
			let today = new Date();
			// Get First / Last Days of the Month
			const firstOfMonth = new Date( today.getFullYear(), today.getMonth(), 1);
			const endOfMonth = new Date( today.getFullYear(), today.getMonth() + 1, 0);
			// Get Target Month & Year for First/End of Month Calculations
			let targetMonth = today.getMonth() - length;
			let targetYear = today.getFullYear();
			// Get Date Field from Display
			let solrFields = this.solrContentConfig.getSolrFieldsForPostField('date');
			let dateSolrField = utils.arrayFirst(solrFields);
			if ( dateSolrField ) {
				// Get Previous Date based on format
				switch( format ){
					case 'day':
						today.setDate(today.getDate() - length);
						break;
					case 'month':
						// If Today is the Last Day of Month, Make sure we get the right date for the end of month
						if( endOfMonth.getDate() === today.getDate() ) {
							while (targetMonth < 0) {
								targetMonth += 12;
								targetYear--;
							}
							today = new Date(targetYear, targetMonth + 1, 0);
						} else if ( firstOfMonth.getDate() === today.getDate() ) {
							while (targetMonth < 0) {
								targetMonth += 12;
								targetYear--;
							}
							today = new Date(targetYear, targetMonth, 1);
						} else {
							today.setMonth( today.getMonth() - length );
						}
						break;
					case 'year':
						today.setFullYear( today.getFullYear() - length );
						break
				}
				// Convert Date to UTC -- Need to Address Time Zone Difference
				today.setUTCHours(0,0,0,0);
				// Should probably add more logic to allow for different date fields
				this.queryBase.fq.push( dateSolrField + ':' + `[${today.toISOString()} TO NOW]`);
			}

		}

		// post status filters
		if (!utils.toBoolean(this.widgetOptions.status.archived)) {
			//this.queryBase.fq.push('-cdc_status_str:[* TO *] OR !cdc_status_str:on');
		}
		if (this.widgetOptions.status.language) {
			this.queryBase.fq.push('cdc_sys_lang_str:' + utils.cleanTerm(this.widgetOptions.status.language));
		}

		// Process sort
		if (this.widgetOptions.display.initialSort) {
			let postField = this.widgetOptions.display.initialSort;
			let sortFields = utils.toArray(this.solrContentConfig.getSolrFieldsForPostField(postField));
			let sortOrder = 'asc' === this.widgetOptions.display.sortOrder ? 'asc' : 'desc';
			if (sortFields.length) {
				this.queryBase.sort = sortFields.map(sortField => sortField + ' ' + sortOrder).join(',');
			}
		}

		// custom fqs
		if (this.widgetOptions.advanced?.customFq) {
			let customFq = String(this.widgetOptions.advanced.customFq).trim();
			let customFqs = customFq.split(/[\n\r]+/);
			customFqs.forEach(fq => {
				if (fq.trim()) {
					this.queryBase.fq.push(fq.trim());
				}
			})
		}

		// Process limit
		let count = Math.min(utils.toNumber(this.limit), POST_LIMIT);
		if (0 < count) {
			this.queryBase.rows = count;
		}

		config.debug && console.info('solrContentConfig: ', this.solrContentConfig, this.widgetId);
	}

	/**
	 * Read query params from Components, translate to a SOLR query
	 * @param {object} formData Any search form values we want to override, if any
	 */
	buildQuery(formData) {
		let query = {
			fq: [],
		};

		let page = 1;

		if (formData.page) {
			page = Math.max(utils.toNumber(formData.page), 1);
		}

		if (this.pagination) {
			query.rows = this.pageSize;
			query.start = (page - 1) * this.pageSize;

			if (this.limit) {
				let pageSizeLimit = this.limit - ((page - 1) * this.pageSize);
				if (pageSizeLimit < query.rows) {
					query.rows = pageSizeLimit;
				}
			}
		}

		// @TODO: limit to first query?
		if (this.widgetOptions.filters?.length && (! this.queryCount || this.inEditor)) {
			let filterFacets = this.taxOptions.getSolrFields();
			if (filterFacets && filterFacets.length) {
				query.facet = 'on';
				query['facet.mincount'] = 1;
				query['facet.field'] = filterFacets;
				query['facet.limit'] = FACET_LIMIT;
			}
		}

		if (formData.filters) {
			query.fq = query.fq.concat(this.taxOptions.getSolrFilterQueries(formData.filters));
		}

		// when not debugging, limit response
		if (!config.debug) {
			query.echoParams = 'none';
			query.indent = 'false';
		}

		let searchQuery = utils.cleanTerm(formData.search || '');
		if (searchQuery) {
			query.q = searchQuery;
		}
		return query;
	}

	// combines 2 Solr querys
	_mergeQueries(a, b) {
		let queries = [a, b];
		let newQuery = { fq: [] };
		if ('object' !== typeof b) {
			return a;
		}
		if ('object' !== typeof a) {
			return b;
		}
		queries.forEach((query) => {
			for (let key in query) {
				let value = query[key];
				if (!query.hasOwnProperty(key)) {
					continue;
				}
				// Array fields are merged, otherwise treat as a string
				if (Array.isArray(value)) {
					if (!newQuery[key]) {
						newQuery[key] = [];
					}
					if (!Array.isArray(newQuery[key])) {
						newQuery[key] = utils.toArray(newQuery[key]);
					}
					newQuery[key] = newQuery[key].concat(value);
				} else if (Array.isArray(newQuery[key])) {
					newQuery[key] = newQuery[key].concat(value);
				} else {
					newQuery[key] = value;
				}
			}
		});
		return newQuery;
	}

	/**
	 * Run actual Solr query against widget state / form data
	 *
	 * @param {*} formData
	 */
	query(formData) {
		formData = Object.assign({}, formData);

		let formQuery = this.buildQuery(formData);
		let query = this._mergeQueries(this.queryBase, formQuery);

		let isEmpty = (!formData.start && (!formData.filters || utils.isEmpty(formData.filters)));

		// At this point, query is an object that closely resembles the SOLR request, so flatten and make the request
		return new Promise((resolve, reject) => {
			// if query isn't valid in editor, return empty response
			if (!this.validQuery) {
				return resolve(this._buildResponse(null))
			}

			let parts = [];
			// Up front, q always goes first
			if (query.q) {
				parts.push('q=' + utils.maybeJoin(query.q, '').trim());
				delete query.q;
			}
			// Now do the remaining parameters
			for (let key in query) {
				let value = query[key];
				if ('fl' === key) {
					parts.push('fl=' + utils.maybeJoin(value, ',').trim());
				} else if (Array.isArray(value)) {
					value.forEach((elValue) => {
						let val = String(elValue).trim();
						if (key && val) {
							parts.push(key + '=' + val);
						}
					});
				} else {
					parts.push(key + '=' + utils.maybeJoin(value, '').trim());
				}
			}

			// Get an MD5 of the full SOLR request url and use it to cache the response for the session
			let solrRequestUrl = this.solrURL + '?' + parts.join('&');
			let hash = md5(solrRequestUrl);
			let results = this._getCache(hash);
			// If already cached, return results here
			if (results) {
				config.debug && console.debug('cc cached query:', hash, solrRequestUrl, this.widgetId);
				return resolve(results);
			}

			// send SOLR query back in editor if empty query (no page or filter specified)
			if (this.inEditor && $ && isEmpty) {
				// only want to save public SOLR request url
				let publicSolrRequestUrl = solrRequestUrl;
				if (this.solrInternal) {
					publicSolrRequestUrl = publicSolrRequestUrl.replace(config.solrURLInternal, config.solrURL);
				}
				$('content-collection-editor:first').trigger('ccQuery', publicSolrRequestUrl);
				config.debug && console.info('cc solr query update: ', publicSolrRequestUrl);
			}

			config.debug && console.info('cc solr query: ', solrRequestUrl, this.widgetId);
			return this.runQuery(solrRequestUrl)
				.then((response) => {
					let builtResults = this._buildResponse(response);
					this._setCache(hash, builtResults);
					this.queryCount++;
					resolve(builtResults);
				})
				.catch((error) =>
					reject({
						total: 0,
						results: [],
						error,
					})
				);
		});
	}

	/**
	 * If given a static SOLR response JS object, load as if returned by SOLR
	 * @param {object} staticData Stati
	 */
	loadStaticData(staticData) {
		try {
			let builtResults = this._buildResponse(staticData);
			let max = this.pageSize;
			if (this.limit) {
				max = Math.min(max, this.limit)
			}
			if (Array.isArray(builtResults.results)) {
				builtResults.results = builtResults.results.slice(0, max);
			}
			return builtResults;
		} catch (error) {
			return {
				total: 0,
				results: [],
				error,
			};
		}
	}

	runQuery(solrRequestUrl) {
		this.queryIndex++;
		if (config.crossdomain || solrRequestUrl.match(/:\d+\//)) {
			return axios({
				url: solrRequestUrl,
				adapter: jsonpAdapter,
				callbackParamName: 'json.wrf',
			});
		} else {
			return axios({
				url: solrRequestUrl,
			});
		}
	}

	// Compiles structured response from raw SOLR response
	_buildResponse(solrResponse) {
		// from solr this is wrapped by Axios response object
		// from static, this is straight response
		solrResponse = solrResponse && solrResponse.data ? solrResponse.data : solrResponse;

		let response = {
			results: [],
			total: utils.toNumber(solrResponse?.response?.numFound || 0),
		};

		// for facet terms, map back from solr field to filter id
		if (!this.queryCount) {
			let facetFields = Object.assign({}, solrResponse?.facet_counts?.facet_fields);
			response.filters = this.taxOptions.loadFilters(facetFields);
		}

		let rawResults = utils.toArray(solrResponse?.response?.docs);
		rawResults.forEach((rawResult, i) => {
			let result = this._buildResult(rawResult);
			if (result) {
				response.results.push(result);
			}
		});

		return response;
	}

	_buildResult(rawResult) {
		if (!utils.isObject(rawResult)) {
			return false;
		}
		if (!rawResult.id) {
			console.error('cc: result has no id', rawResult);
			return false;
		}
		return this.solrContentConfig.getFieldsFromSolrResult(rawResult);
	}

	_getCache(key) {
		return cache.queries[key];
	}

	_setCache(key, value) {
		// limit how many are saved
		let keys = utils.objectKeys(cache.queries);
		if (CACHE_LIMIT < keys.length) {
			keys = keys.slice(0, 0 - CACHE_LIMIT - 1);
			keys.forEach((oldKey) => {
				delete cache.queries[oldKey];
			});
		}
		cache.queries[key] = value;
	}

	/**
	 * Helper method to convert an object to a SOLR / lucene query
	 *
	 * @param {object} object  Source object
	 * @param {string} join    (default AND) AND or OR - how to join items in object
	 * @param {string} subJoin (default OR) AND or OR - join for subset arrays and objects
	 * @param {number} level   Track recursion to prevent endless loop, handle parentheses
	 * @returns {string}
	 */
	_objToQuery(object, join, subJoin, level) {
		let parts = [];
		level = level || 1;
		// limit recursion
		if (8 < level) {
			console.error('cc: recursion issue: ', object);
			return '';
		}
		join = join || AND;
		subJoin = subJoin || OR;
		if (!utils.isObject(object)) {
			return '';
		}
		for (let field in object) {
			let part = '';
			let value = object[field];
			if (!object.hasOwnProperty(field)) {
				continue;
			}
			if (Array.isArray(value)) {
				if (!value.length) {
					continue;
				}
				let arrayJoiner = AND === subJoin ? ' AND ' : ',';
				part = field + ':(' + value.map((item) => '"' + item + '"').join(arrayJoiner) + ')';
			} else if ('object' === typeof value) {
				part = this._objToQuery(value, subJoin, subJoin, level + 1);
			} else {
				part = field + ':' + utils.cleanString(value);
			}
			if (part) {
				parts.push(part);
			}
		}
		let query = parts.join(' ' + join + ' ');
		if (1 < level && 1 < parts.length) {
			query = '(' + query + ')';
		}
		return query;
	}

	/**
	 * Helper method to convert an array object to a SOLR / lucene query
	 * Accepts arrays like:
	 * [ "field:value", "field2:value2" ]
	 *
	 * @param {array} array  Source array
	 * @param {string} join  AND or OR
	 * @param {string} subJoin AND or OR - join for subset arrays and objects
	 * @param {number} level Track recursion to prevent endless loop, handle parentheses
	 * @returns {string}
	 */
	_arrayToQuery(array, join, subJoin, level) {
		let parts = [];
		join = join || OR;
		subJoin = subJoin || OR;
		level = level || 1;
		// limit recursion
		if (8 < level) {
			console.error('cc: recursion issue: ', array);
			return '';
		}
		if (!Array.isArray(array)) {
			return '';
		}
		array.forEach((arrayItem) => {
			let part = '';
			if (Array.isArray(arrayItem)) {
				part = this._arrayToQuery(arrayItem, subJoin, subJoin, level + 1);
			} else if ('object' === typeof arrayItem) {
				part = this._objToQuery(arrayItem, subJoin, subJoin, level + 1);
			} else if ('string' === typeof arrayItem) {
				part = arrayItem.trim();
				// // when just a string, must have a colon to signify a SOLR query
				// let itemParts = arrayItem.split(/:/);
				// if (2 <= itemParts.length) {
				// 	part = utils.cleanString(itemParts[0]) + ':' + utils.cleanString(itemParts[1]);
				// }
			}
			if (part) {
				parts.push(part);
			}
		});
		let query = '(' + parts.join(' ' + join + ' ') + ')';
		// remove superfluous parentheses
		query = query.replace(/^\(\((.*)\)\)$/,'($1)');
		return query;
	}
}

export default Solr;
